import { useState } from "react";
import { LayoutGrid, Search, ShoppingCart } from "lucide-react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { StickyCartPreview } from "./StickyCartPreview";
import { useCart } from "../context/CartContext";

export function AppShell({ children, onSelectCategory, selectedCategory }) {
  const { cartCount, toast } = useCart();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isCartPreviewOpen, setIsCartPreviewOpen] = useState(false);
  const isCartFlowRoute = location.pathname.includes("/cart") || location.pathname.includes("/checkout");
  // Category/sidebar state is intentionally kept at App level for easy future rollback.
  // The current platter UI hides left navigation and uses the header search only.
  void selectedCategory;

  function handleHeaderSearch(event) {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const query = String(formData.get("search") || "").trim();
    navigate(query ? `/products?search=${encodeURIComponent(query)}` : "/products");
  }

  function handleProductsClick() {
    onSelectCategory(null);
  }

  return (
    <div className="min-h-screen bg-background pb-20 sm:pb-0">
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur-xl">
        <div className="mx-auto grid max-w-[1480px] grid-cols-[auto_1fr_auto] items-center gap-3 px-4 py-3 sm:gap-5 sm:px-6 lg:px-8">
          <Link className="flex min-w-0 items-center" onClick={handleProductsClick} to="/products">
            <img alt="Logo" className="brand-logo" src={`${process.env.PUBLIC_URL}/logo.png`}/>
          </Link>

          <form onSubmit={handleHeaderSearch}>
            <label className="relative block">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                className="h-11 w-full rounded-xl border border-border bg-background px-11 text-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/15"
                defaultValue={searchParams.get("search") || ""}
                name="search"
                placeholder="Search products for pickup..."
                type="search"
              />
            </label>
          </form>

          <Link
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-semibold shadow-sm transition hover:border-primary sm:hidden"
            to="/cart"
          >
            <ShoppingCart className="h-4 w-4" />
            <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
              {cartCount}
            </span>
          </Link>

          <button
            className="hidden h-11 items-center justify-center gap-2 rounded-xl border border-border bg-background px-4 text-sm font-semibold shadow-sm transition hover:border-primary sm:inline-flex"
            onClick={() => {
              if (!isCartFlowRoute) {
                setIsCartPreviewOpen(true);
              }
            }}
            type="button"
          >
            <ShoppingCart className="h-4 w-4" />
            <span>Cart</span>
            <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
              {cartCount}
            </span>
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-[1480px] px-4 py-5 sm:px-6 lg:px-8">
        <main className="min-w-0">{children}</main>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-3 py-2 backdrop-blur-xl sm:hidden">
        <div className="mx-auto grid max-w-md grid-cols-2 gap-2">
          <Link
            className="flex flex-col items-center justify-center rounded-xl px-2 py-2 text-xs font-semibold text-muted-foreground transition hover:bg-secondary"
            onClick={handleProductsClick}
            to="/products"
          >
            <LayoutGrid className="h-4 w-4" />
            <span className="mt-1">Products</span>
          </Link>
          <Link
            className="flex flex-col items-center justify-center rounded-xl px-2 py-2 text-xs font-semibold text-muted-foreground transition hover:bg-secondary"
            to="/cart"
          >
            <span className="relative">
              <ShoppingCart className="h-4 w-4" />
              <span className="absolute -right-3 -top-2 rounded-full bg-primary px-1.5 py-0.5 text-[10px] leading-none text-primary-foreground">
                {cartCount}
              </span>
            </span>
            <span className="mt-1">Cart</span>
          </Link>
        </div>
      </nav>

      {isCartPreviewOpen && !isCartFlowRoute ? (
        <div
          className="fixed inset-x-0 bottom-0 top-[82px] z-50 hidden bg-foreground/40 backdrop-blur-sm sm:block lg:top-[90px]"
          onClick={() => setIsCartPreviewOpen(false)}
        >
          <div
            className="absolute inset-x-0 bottom-0 max-h-[88vh] overflow-y-auto rounded-t-3xl border border-border bg-card p-4 shadow-soft sm:bottom-4 sm:left-auto sm:right-4 sm:top-4 sm:w-[390px] sm:rounded-3xl"
            onClick={(event) => event.stopPropagation()}
          >
            <StickyCartPreview
              compact
              onCheckout={() => setIsCartPreviewOpen(false)}
              onClose={() => setIsCartPreviewOpen(false)}
            />
          </div>
        </div>
      ) : null}

      {toast ? (
        <div className="fixed right-4 top-24 z-50 rounded-xl bg-foreground px-4 py-3 text-sm font-semibold text-background shadow-soft">
          {toast}
        </div>
      ) : null}
    </div>
  );
}
